/**
 * Auto-reconnect policy for the voice Telnyx session.
 *
 * When a live session errors or its socket drops, we transparently try to
 * bring it back up a few times before giving up and dropping the operator
 * to offline — so a brief network blip doesn't force them to manually toggle
 * Available again, while a persistent failure still stops instead of looping
 * forever.
 */
export const MAX_RECONNECT_ATTEMPTS = 5;

/** Delay between a drop and the next reconnect attempt. */
export const RECONNECT_DELAY_MS = 2000;

/**
 * Why a session went down. `sign-in` means our server couldn't hand back a
 * Telnyx token, so no connection ever existed to lose.
 */
export type ConnectionDropCause = "connection" | "sign-in";

export type ReconnectDecision =
  | { readonly kind: "retry"; readonly attempt: number }
  | { readonly kind: "give-up" };

/**
 * Decide the next move given how many reconnect attempts have already been
 * made since the last clean connection. Returns the 1-based number of the
 * attempt to make next, or `give-up` once the budget is exhausted.
 */
export const planReconnect = (
  attemptsMade: number,
  maxAttempts: number = MAX_RECONNECT_ATTEMPTS,
): ReconnectDecision =>
  attemptsMade >= maxAttempts
    ? { kind: "give-up" }
    : { kind: "retry", attempt: attemptsMade + 1 };

export const describeReconnectAttempt = (
  cause: ConnectionDropCause,
  attempt: number,
): string =>
  cause === "sign-in"
    ? `Couldn't sign in — retrying (${attempt}/${MAX_RECONNECT_ATTEMPTS})…`
    : `Connection lost — reconnecting (${attempt}/${MAX_RECONNECT_ATTEMPTS})…`;

export const describeReconnectGiveUp = (cause: ConnectionDropCause): string =>
  `Couldn't ${cause === "sign-in" ? "sign in" : "reconnect"} after ${MAX_RECONNECT_ATTEMPTS} attempts. Set yourself available to try again.`;
